import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Box, Container, Grid, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { blogcontent } from '../../../assets';
import { blogData } from './blogdata';

export const slugify = (text: string): string =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "");

const SingleBlog: React.FC = () => {
  const { slug } = useParams();
  const blog = blogData.find((b) => slugify(b.title) === slug);

  if (!blog) {
    return (
      <Container>
        <Typography variant="h5" mt={6}>
          Blog not found
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ pb: 5 }}>
      <Container>
        <Box sx={{ mt: 6 }}>
          <Box mt={7} textAlign="start">
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "600", color: "primary.main" }}
            >
              {blog.title}
            </Typography>
          </Box>

          {/* Author and Date */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: "15px",
              alignItems: "center",
            }}
            mt={2}
          >
            {" "}
            {/* Align items vertically centered */}
            <img
              src={blog.authorImage}
              alt="Top Real Estate Agencies in Hyderabad"  //Leslie Alexander
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                marginRight: "10px",
              }}
            />{" "}
            <Link to="" style={{ textDecoration: "none" }}>
              <Typography
                variant="body2"
                sx={{ color: "black", pr: 2, borderRight: "2px solid #ccc" }}
              >
                {blog.author}
              </Typography>
            </Link>

            <Link to="" style={{ textDecoration: "none" }}>
              <Typography
                variant="body2"
                sx={{ color: "black", pr: 2, borderRight: "2px solid #ccc" }}
              >
                {blog.date}
              </Typography>
            </Link>
          </Box>

          {/* Main Image */}
          <Box
            sx={{
              width: "100%",
              overflow: "hidden",
              mt: 7,
              height: {
                xs: "auto",
                md: "auto",
              },
            }}
          >
            <img
              src={blog.bannerImage}
              alt="Blog Visual"
              style={{ width: "100%", height: "100%" }}
            />
          </Box>

          <Grid container spacing={2}>
            {/* Intro */}
            {blog.content.intro.map((paragraph, index) => (
              <Grid item xs={12} key={index}>
                <Typography variant="body2" textAlign="justify" mt={2}>
                  {paragraph}
                </Typography>
              </Grid>
            ))}

            {/* Sections with conditional rendering */}
            {blog.content.sections.map((section, index) => (
              <Grid item xs={12} key={index}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  mt={4}
                  fontSize={{ xs: '1rem', lg: '1.2rem', textAlign: 'start' }}
                >
                  {section.title}
                </Typography>

                {/* ✅ CheckList Style */}
                {section.type === 'checklist' && (
                  <Grid container spacing={2} mt={2}>
                    {section.content.map((text, subIndex) => (
                      <Grid item xs={12} key={subIndex}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'start',
                            justifyContent: { xs: 'flex-start', lg: 'flex-start' },
                          }}
                        >
                          <CheckCircleIcon
                            sx={{
                              fontSize: '20px',
                              backgroundColor: 'black',
                              color: 'white',
                              borderRadius: '50%',
                              padding: '8px',
                              marginRight: '10px',
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: 'black', textAlign: 'start', flexGrow: 1 }}
                          >
                            {text}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {/* ✅ Bullet List Style */}
                {section.type === 'bullet' && (
                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={12}>
                      <ul style={{ marginTop: '12px', color: '#333', paddingLeft: '1.25rem' }}>
                        {section.content.map((text, bulletIndex) => (
                          <li key={bulletIndex} style={{ marginBottom: '8px' }}>
                            <Typography
                              variant="body2"
                              sx={{ color: 'black', fontSize: { xs: '18px', lg: '16px' }, textAlign: 'start' }}
                            >
                              {text}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </Grid>
                  </Grid>
                )}

                {/* ✅ Default Paragraph Style */}
                {!section.type || section.type === 'default' ? (
                  section.content.map((text, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      textAlign="justify"
                      mt={2}
                    >
                      {text}
                    </Typography>
                  ))
                ) : null}
              </Grid>
            ))}

            

            {/* Conclusion */}
            <Grid item xs={12} mt={4}>
              <Typography variant="h6" fontWeight={700} sx={{ textAlign: 'start' }}>
                Conclusion
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#30779d',
                  borderLeft: '4px solid black',
                  padding: '16px',
                  borderRadius: '4px',
                  mt: 2,
                  px: { xs: 3.3, sm: 5, md: 7 },
                  py: { xs: 3.3, sm: 5, md: 4 },
                }}
              >
                {blog.content.conclusion.map((line, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      color: 'white',
                      textAlign: 'justify',
                      fontWeight:
                        index === blog.content.conclusion.length - 1 ? 600 : 'normal',
                      mt: index > 0 ? 2 : 0,
                    }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
          </Grid>

        </Box>
      </Container>
    </Box>
  );
};

export default SingleBlog;
